import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, CertCounts, CertSpec, Question, api } from "../lib/api";
import { largestRemainder } from "../lib/quota";
import { useMe } from "../lib/useMe";

const DIFFICULTY_LABELS: Record<number, string> = { 1: "Facile", 2: "Moyen", 3: "Difficile" };

export default function Authoring() {
  const { me, loading: meLoading } = useMe();

  const [certs, setCerts] = useState<CertSpec[]>([]);
  const [certId, setCertId] = useState("");
  const [domain, setDomain] = useState("");
  const [n, setN] = useState(5);
  const [difficulty, setDifficulty] = useState<1 | 2 | 3>(2);
  const [answerCount, setAnswerCount] = useState(1);

  const [pending, setPending] = useState<Question[]>([]);
  const [counts, setCounts] = useState<CertCounts[]>([]);
  const [target, setTarget] = useState(200);
  const [generating, setGenerating] = useState(false);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const selectedCert = useMemo(() => certs.find((c) => c.id === certId), [certs, certId]);

  const loadPending = useCallback(
    (cid?: string) =>
      api
        .pendingQuestions(cid || undefined)
        .then(setPending)
        .catch(() => setPending([])),
    [],
  );

  useEffect(() => {
    if (!me?.is_admin) return;
    api.listCerts().then((list) => {
      setCerts(list);
      if (list.length > 0) {
        setCertId(list[0].id);
        setDomain(list[0].domains[0]?.name ?? "");
      }
    });
    loadPending();
    api.questionCounts().then(setCounts).catch(() => setCounts([]));
  }, [me, loadPending]);

  useEffect(() => {
    if (selectedCert) setDomain(selectedCert.domains[0]?.name ?? "");
  }, [selectedCert]);

  if (meLoading) return <div className="p-8 text-ink-700">Chargement…</div>;
  if (!me?.is_admin) {
    return (
      <div className="card">
        <h1 className="font-display text-2xl mb-2">Atelier</h1>
        <p className="text-sm text-ink-700">
          Accès réservé aux administrateurs. Cette section permet de générer et de valider les
          questions produites par Claude.
        </p>
      </div>
    );
  }

  const handleGenerate = async () => {
    if (!certId || !domain) return;
    setGenerating(true);
    setError(null);
    try {
      await api.generateQuestions({
        cert_id: certId,
        domain,
        n,
        difficulty,
        answer_count: answerCount,
      });
      await loadPending();
      api.questionCounts().then(setCounts).catch(() => {});
    } catch (e) {
      setError(e instanceof ApiError ? `Erreur ${e.status} : ${e.message}` : "Erreur de génération");
    } finally {
      setGenerating(false);
    }
  };

  const handleReview = async (qid: string, approve: boolean) => {
    setBusyIds((prev) => new Set(prev).add(qid));
    try {
      await api.reviewQuestion(qid, approve);
      setPending((prev) => prev.filter((q) => q.id !== qid));
      api.questionCounts().then(setCounts).catch(() => {});
    } catch {
      setError("Échec de la revue, réessayez.");
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(qid);
        return next;
      });
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl mb-6">Atelier</h1>

      {/* ─── Génération ─── */}
      <div className="card mb-8">
        <h2 className="font-display text-xl mb-4">Générer des questions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm">
            <span className="text-scribe-600">Certification</span>
            <select
              className="input mt-1 w-full"
              value={certId}
              onChange={(e) => setCertId(e.target.value)}
            >
              {certs.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-scribe-600">Domaine</span>
            <select
              className="input mt-1 w-full"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
            >
              {selectedCert?.domains.map((d) => (
                <option key={d.id} value={d.name}>
                  {d.name} ({(d.weight * 100).toFixed(0)}%)
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-scribe-600">Nombre (max 6 recommandé)</span>
            <input
              type="number"
              min={1}
              max={20}
              className="input mt-1 w-full"
              value={n}
              onChange={(e) => setN(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
            />
          </label>
          <label className="block text-sm">
            <span className="text-scribe-600">Difficulté</span>
            <select
              className="input mt-1 w-full"
              value={difficulty}
              onChange={(e) => setDifficulty(Number(e.target.value) as 1 | 2 | 3)}
            >
              {[1, 2, 3].map((d) => (
                <option key={d} value={d}>
                  {DIFFICULTY_LABELS[d]}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="text-scribe-600">Bonnes réponses par question</span>
            <select
              className="input mt-1 w-full"
              value={answerCount}
              onChange={(e) => setAnswerCount(Number(e.target.value))}
            >
              {[1, 2, 3, 4].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-end">
            <button
              onClick={handleGenerate}
              disabled={generating || !certId || !domain}
              className="btn-primary w-full disabled:opacity-50"
            >
              {generating ? "Génération en cours…" : "Générer via Claude"}
            </button>
          </div>
        </div>
        {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
      </div>

      {/* ─── Couverture de la banque ─── */}
      {selectedCert && (
        <div className="card mb-8">
          <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2 mb-4">
            <h2 className="font-display text-xl">Banque — {selectedCert.code}</h2>
            <label className="text-sm flex items-center gap-2">
              <span className="text-scribe-600">Objectif total</span>
              <input
                type="number"
                min={selectedCert.domains.length}
                className="input w-24"
                value={target}
                onChange={(e) => setTarget(Math.max(0, Number(e.target.value) || 0))}
              />
            </label>
          </div>
          {(() => {
            const certCounts = counts.find((c) => c.cert_id === selectedCert.id);
            const quotas = largestRemainder(selectedCert.domains, target);
            return (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-mono uppercase tracking-wider text-scribe-600 text-left">
                    <th className="pb-2 font-medium">Domaine</th>
                    <th className="pb-2 font-medium text-right">Validées</th>
                    <th className="pb-2 font-medium text-right">En attente</th>
                    <th className="pb-2 font-medium text-right">Objectif</th>
                    <th className="pb-2 font-medium text-right">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCert.domains.map((d, i) => {
                    const row = certCounts?.domains.find((x) => x.domain === d.name);
                    const reviewed = row?.reviewed ?? 0;
                    const pend = row?.pending ?? 0;
                    const quota = quotas[i];
                    const delta = quota - reviewed - pend;
                    return (
                      <tr
                        key={d.id}
                        className={`border-t border-papyrus-200 ${delta > 0 ? "cursor-pointer hover:bg-papyrus-50" : ""}`}
                        title={delta > 0 ? "Cliquer pour pré-remplir la génération" : undefined}
                        onClick={() => {
                          if (delta <= 0) return;
                          setDomain(d.name);
                          setN(Math.min(6, delta));
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                      >
                        <td className="py-2 pr-2 min-w-0 truncate">{d.name}</td>
                        <td className="py-2 text-right font-mono">{reviewed}</td>
                        <td className="py-2 text-right font-mono text-scribe-600">{pend}</td>
                        <td className="py-2 text-right font-mono">{quota}</td>
                        <td
                          className={`py-2 text-right font-mono ${delta > 0 ? "text-red-700" : "text-green-700"}`}
                        >
                          {delta > 0 ? `+${delta}` : "OK"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
          <p className="text-xs text-scribe-600 mt-3">
            Objectifs répartis au prorata des poids officiels (méthode du plus fort reste). Cliquer
            une ligne en déficit pré-remplit le formulaire (max 6 par fournée).
          </p>
        </div>
      )}

      {/* ─── File de revue ─── */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl">
          En attente de validation{" "}
          <span className="font-mono text-sm text-scribe-600">({pending.length})</span>
        </h2>
        <button onClick={() => loadPending()} className="text-sm text-scribe-600 hover:text-ink-800">
          Rafraîchir
        </button>
      </div>

      {pending.length === 0 ? (
        <p className="text-sm text-ink-700">Aucune question en attente.</p>
      ) : (
        <div className="space-y-4">
          {pending.map((q) => (
            <div key={q.id} className="card">
              <div className="text-xs font-mono text-scribe-600 uppercase tracking-wider mb-2">
                {q.cert_id} · {q.domain} · {DIFFICULTY_LABELS[q.difficulty]} ·{" "}
                {q.type === "multi_choice" ? "choix multiple" : "choix unique"}
              </div>
              <p className="text-sm leading-relaxed mb-3 whitespace-pre-wrap">{q.stem}</p>
              <ul className="space-y-1.5 mb-3">
                {q.options.map((opt) => {
                  const correct = q.correct_option_ids.includes(opt.id);
                  return (
                    <li
                      key={opt.id}
                      className={`text-sm rounded px-3 py-2 border ${
                        correct
                          ? "border-lapis-500 bg-lapis-500/10 font-medium"
                          : "border-papyrus-200"
                      }`}
                    >
                      <span className="font-mono text-xs mr-2 text-scribe-600">{opt.id}.</span>
                      {opt.text}
                      {correct && <span className="ml-2 text-lapis-500">✓</span>}
                    </li>
                  );
                })}
              </ul>
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-scribe-600 hover:text-ink-800">
                  Explication
                </summary>
                <p className="mt-2 text-sm text-ink-700 leading-relaxed whitespace-pre-wrap">
                  {q.explanation}
                </p>
                {Object.keys(q.option_explanations ?? {}).length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-ink-700">
                    {q.options.map((opt) =>
                      q.option_explanations[opt.id] ? (
                        <li key={opt.id}>
                          <span className="font-mono text-scribe-600 mr-1">{opt.id}.</span>
                          {q.option_explanations[opt.id]}
                        </li>
                      ) : null,
                    )}
                  </ul>
                )}
                {q.sources.length > 0 && (
                  <ul className="mt-2 text-xs text-scribe-600 list-disc pl-5">
                    {q.sources.map((s) => (
                      <li key={s}>{s}</li>
                    ))}
                  </ul>
                )}
              </details>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:justify-end">
                <button
                  onClick={() => handleReview(q.id, false)}
                  disabled={busyIds.has(q.id)}
                  className="btn-secondary disabled:opacity-50"
                >
                  Rejeter
                </button>
                <button
                  onClick={() => handleReview(q.id, true)}
                  disabled={busyIds.has(q.id)}
                  className="btn-primary disabled:opacity-50"
                >
                  Approuver
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
